import { useState, useMemo, useEffect } from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { useAsync } from 'react-async';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import styles from './CustomerList.module.sass';
import Card from '../../components/Card';
import Form from '../../components/Form';
import Table from './Table';
import { exportUsersToExcel, getUserList, updateBulkUserScore } from 'core/services/komagebi/users';
import Icon from 'components/Icon';
import { paths } from 'router/paths';
import Filters from 'components/Filters';
import Settings from './Settings';
import { IUserListItem } from '@types';
import Modal from 'components/Modal';
import { OverlayLoader } from 'components/Loader';
import TextInput from 'components/TextInput';

import { getErrorMessage, RoleType } from '@core';
import { useAppSelector } from 'store';
import { accountSelectors } from 'store/account';

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100, 200, 500];
const ITEMS_PER_PAGE_STORAGE_KEY = 'userList_itemsPerPage';

interface FilterOptions {
  gender: string | null;
  city: string | null;
  ranking: string | null;
}

const schema = yup.object().shape({
  score: yup.number().required('სავალდებულოა'),
  userIds: yup.array().of(yup.string()).required('სავალდებულოა'),
  description: yup.string().required('სავალდებულოა'),
});

const UserListPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const authorizedUser = useAppSelector(accountSelectors.userSelector);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    // Initialize from localStorage or default to 50
    try {
      const savedItemsPerPage = localStorage.getItem(ITEMS_PER_PAGE_STORAGE_KEY);
      return savedItemsPerPage ? parseInt(savedItemsPerPage, 10) : 50;
    } catch (error) {
      return 50; // Fallback to default if localStorage is not available
    }
  });

  const [visibleScoreUpdateModal, setVisibleScoreUpdateModal] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({ gender: null, city: null, ranking: null });
  const { data: usersResponse, isLoading: isUserListLoading, reload } = useAsync(getUserList);
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<{ score: number; userIds: string[]; description: string }>({ resolver: yupResolver(schema) });

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Filter users based on search and filters
  const filteredUsers = useMemo(() => {
    if (!usersResponse) return [];

    let filtered = usersResponse;

    // Apply search filter
    if (search.trim()) {
      filtered = filtered.filter(
        (user: IUserListItem) =>
          `${user.firstName} ${user.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
          user.email?.toLowerCase().includes(search.toLowerCase()) ||
          user.phoneNumber?.toLowerCase().includes(search.toLowerCase()),
      );
    }

    // Apply gender filter
    if (filters.gender) {
      filtered = filtered.filter((user: IUserListItem) => user.gender === filters.gender);
    }

    // Apply ranking filter
    if (filters.ranking) {
      filtered = filtered.filter((user: IUserListItem) => user.rank_name === filters.ranking);
    }

    // Apply city filter
    if (filters.city) {
      filtered = filtered.filter((user: IUserListItem) => user.city === filters.city);
    }

    return filtered;
  }, [usersResponse, search, filters]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Check if all current page users are selected
  const allCurrentPageSelected = paginatedUsers.length > 0 && paginatedUsers.every(user => selectedUsers.has(user.id));

  // Check if some current page users are selected
  const someCurrentPageSelected = paginatedUsers.some(user => selectedUsers.has(user.id));

  const handleExport = () => {
    void exportUsersToExcel(search);
  };

  const handleFilterChange = (options: FilterOptions) => {
    setFilters(options);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected + 1);
  };

  const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page

    // Save to localStorage
    try {
      localStorage.setItem(ITEMS_PER_PAGE_STORAGE_KEY, newItemsPerPage.toString());
    } catch (error) {
      // Silently fail if localStorage is not available
      console.warn('Could not save items per page preference to localStorage');
    }
  };

  const handleSelectAll = () => {
    const newSelectedUsers = new Set(selectedUsers);

    if (allCurrentPageSelected) {
      // Deselect all current page users
      paginatedUsers.forEach(user => newSelectedUsers.delete(user.id));
    } else {
      // Select all current page users
      paginatedUsers.forEach(user => newSelectedUsers.add(user.id));
    }

    setSelectedUsers(newSelectedUsers);
  };

  const handleSelectUser = (userId: string, isSelected: boolean) => {
    const newSelectedUsers = new Set(selectedUsers);

    if (isSelected) {
      newSelectedUsers.add(userId);
    } else {
      newSelectedUsers.delete(userId);
    }

    setSelectedUsers(newSelectedUsers);
  };

  const handleChangeScoreBulk = () => {
    setValue('userIds', Array.from(selectedUsers));
    setVisibleScoreUpdateModal(true);
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  const [isAdding, setIsAdding] = useState(false);

  const onSubmit = () => {
    void handleSubmit(
      data => {
        setIsAdding(true);
        updateBulkUserScore(data)
          .then(() => {
            reload();
            setSelectedUsers(new Set());
            toast.success('განახლდა წარმატებით');
          })
          .catch(() => {
            toast.error('დაფისქირდა შეცდომა');
          })
          .finally(() => {
            setIsAdding(false);
            setVisibleScoreUpdateModal(false);
          });
      },
      (err, event) => {
        console.log(err, event, 'validation errors');
      },
    )();
  };

  return (
    <>
      <Modal
        outerClassName={styles.outer}
        visible={visibleScoreUpdateModal}
        onClose={() => setVisibleScoreUpdateModal(false)}
      >
        <div className={styles.post}>
          {isAdding && <OverlayLoader />}
          <div className={cn('title-green', styles.title)}>ქულის ცვლილება</div>

          <div className={styles.field}>
            <TextInput
              className={styles.field}
              label="ქულა"
              name="score"
              error={getErrorMessage(errors, 'score')}
              type="number"
              register={register}
            />
          </div>

          <div className={styles.field}>
            <TextInput
              className={styles.field}
              label="მიზეზი"
              name="description"
              error={getErrorMessage(errors, 'description')}
              type="text"
              register={register}
            />
          </div>

          <div className={styles.foot}>
            <button onClick={onSubmit} className={cn('button', styles.button)}>
              <span>დადასტურება</span>
              <Icon name="arrow-right" size="24" />
            </button>
          </div>
        </div>
      </Modal>

      <Card
        className={styles.card}
        title={`სია(${filteredUsers.length})`}
        classTitle={cn('title-purple', styles.title)}
        classCardHead={cn(styles.head)}
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={setSearch}
              onSubmit={() => {}}
              placeholder="ძიება"
              type="text"
              name="search"
              icon="search"
            />
            <div className={cn('button', 'button-small', styles.button, styles.exportButton)} onClick={handleExport}>
              <span>ექსპორტი</span>
            </div>
            <Link className={cn('button', 'button-small', styles.button)} to={paths.USER_ADD_PATH}>
              <Icon name="add" size="24" />
              <span>დამატება</span>
            </Link>

            <Filters className={styles.filters} title="ფილტრაცია">
              <Settings changeFilters={handleFilterChange} />
            </Filters>
          </>
        }
      >
        <div className={cn(styles.row)}>
          <div className={styles.paginationControls}>
            <label htmlFor="itemsPerPage" className={styles.itemsPerPageLabel}>
              ელემენტები გვერდზე:
            </label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className={styles.itemsPerPageSelect}
            >
              {ITEMS_PER_PAGE_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {selectedUsers.size > 0 && (
            <div className={styles.bulkControls}>
              <div className={styles.bulkInfo}>
                <span>{selectedUsers.size} მომხმარებელი არჩეულია</span>
                <button className={styles.clearSelection} onClick={clearSelection}>
                  გასუფთავება
                </button>
              </div>
              {authorizedUser?.role === RoleType.SUPERADMIN && (
                <button
                  className={cn('button', 'button-small', styles.bulkActionButton)}
                  onClick={handleChangeScoreBulk}
                >
                  <span>ქულის ცვლილება ({selectedUsers.size})</span>
                </button>
              )}
            </div>
          )}

          <Table
            users={paginatedUsers}
            isRequestLoading={isUserListLoading}
            className={styles.table}
            pageCount={totalPages}
            handlePageChange={handlePageChange}
            selectedUsers={selectedUsers}
            onSelectUser={handleSelectUser}
            onSelectAll={handleSelectAll}
            allCurrentPageSelected={allCurrentPageSelected}
            someCurrentPageSelected={someCurrentPageSelected}
          />
        </div>
      </Card>
    </>
  );
};

export default UserListPage;
